import React, { useState, useEffect } from 'react';
import { FieldHookConfig, useField } from 'formik';

type DateInputProps = FieldHookConfig<Date | null> & {
  label?: string;
  labelPosition?: 'top' | 'left';
  className?: string;
  wrapperClassName?: string;
  readOnly?: boolean;
  onDateChange?: (date: Date | null) => void;
};

const formatDateForInput = (date: Date | string | null | undefined) => {
  if (!date) return '';
  if (typeof date === 'string') {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  }
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().split('T')[0];
};


const DateInput: React.FC<DateInputProps> = ({
  label,
  labelPosition = 'top',
  className = '',
  wrapperClassName = '',
  readOnly = false,
  onDateChange,
  ...props
}) => {
  const [field, meta, helpers] = useField(props);
  const isInvalid = meta.touched && meta.error;

  const [displayValue, setDisplayValue] = useState(formatDateForInput(field.value));

  useEffect(() => {
    setDisplayValue(formatDateForInput(field.value));
  }, [field.value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value ? new Date(e.target.value) : null;

    helpers.setValue(val);
    setDisplayValue(formatDateForInput(val));

    if (onDateChange) {
      onDateChange(val);
    }
  };

  const renderLabel = () => {
    if (!label) return null;

    return (
      <label className={`form-label fw-bold ${labelPosition === 'left' ? 'mb-0' : ''}`}>
        {label}
      </label>
    );
  };

  const inputElement = (
    <div className={labelPosition === 'left' && label ? 'flex-grow-1' : 'w-100'}>
      <input
        type="date"
        className={['form-control', isInvalid && 'is-invalid', className].filter(Boolean).join(' ')}
        value={displayValue}
        onChange={handleChange}
        onBlur={field.onBlur}
        name={field.name}
        disabled={readOnly}
      />
      {isInvalid && <div className="invalid-feedback">{meta.error}</div>}
    </div>
  );

  if (labelPosition === 'left' && label) {
    return (
      <div className={`${wrapperClassName} h-100`}>
        <div className='d-flex align-items-start gap-3 h-100'>
          <div className='flex-shrink-0 pt-2'>
            {renderLabel()}
          </div>
          {inputElement}
        </div>
      </div>
    );
  }

  return (
    <div className={`${wrapperClassName} h-100`}>
      {renderLabel()}
      {inputElement}
    </div>
  );
};

export default DateInput;